import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CommonSideDialog from "../../../common/components/side-dialog/CommonSideDialog";
import CommonCheckBox from "../../../common/components/checkbox/CommonCheckBox";
import Button from "../../../common/components/xepp/button/Button";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { getErrorMessage } from "../../../utils/error-handler";
import { Permission } from "../../../model/roles/role";
import {
	useGetPermissionsQuery,
	useLazyGetRoleByIdQuery,
	useCreateRoleMutation,
	useEditRoleMutation,
} from "../../../service/role-service";
import styles from "./RoleManagement.module.scss";

type Props = {
	mode: "create" | "edit";
	roleId?: string;
	onClose: () => void;
};

const RolePermissionsSideDialog = ({ mode, roleId, onClose }: Props) => {
	const { data: permissions, isLoading: permsLoading, isError: permsError } = useGetPermissionsQuery();
	const [getRoleById, { isFetching: roleLoading }] = useLazyGetRoleByIdQuery();
	const [createRole, { isLoading: creating }] = useCreateRoleMutation();
	const [editRole, { isLoading: editing }] = useEditRoleMutation();

	const [name, setName] = useState("");
	const [selected, setSelected] = useState<Set<number>>(new Set());

	useEffect(() => {
		if (mode === "edit" && roleId) {
			getRoleById(roleId)
				.unwrap()
				.then(role => {
					setName(role.name);
					setSelected(new Set(role.permissions.map(p => p.id)));
				})
				.catch((err: any) => toast.error(getErrorMessage(err?.data) ?? "Failed to load role."));
		}
	}, [mode, roleId, getRoleById]);

	const grouped = useMemo(() => {
		const modules = new Map<string, Map<string, Permission[]>>();
		(permissions ?? []).forEach(p => {
			if (!p.module) return;
			if (!modules.has(p.module)) modules.set(p.module, new Map());
			const subs = modules.get(p.module)!;
			if (!subs.has(p.subModule)) subs.set(p.subModule, []);
			subs.get(p.subModule)!.push(p);
		});
		return modules;
	}, [permissions]);

	const toggle = (id: number) =>
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const handleApply = async () => {
		if (!name.trim()) {
			toast.error("Role name is required.");
			return;
		}
		try {
			if (mode === "edit" && roleId) {
				await editRole({ roleId, name: name.trim(), permissions: Array.from(selected) }).unwrap();
				toast.success("Role successfully updated.");
			} else {
				await createRole({ name: name.trim(), permissions: Array.from(selected) }).unwrap();
				toast.success("Role successfully created.");
			}
			onClose();
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? "Failed to save role.");
		}
	};

	const loading = permsLoading || (mode === "edit" && roleLoading);

	return (
		<CommonSideDialog onCloseDialog={onClose}>
			<div className={styles.dialog}>
				<div className={styles.dialogHeader}>
					<h2 className={styles.dialogTitle}>User Role Name</h2>
					<p className={styles.dialogSubtitle}>Set User Role Permissions below</p>
				</div>

				<div className={styles.dialogBody}>
					<label className={styles.field}>
						<span className={styles.fieldLabel}>User Role Name</span>
						<input
							className={styles.fieldInput}
							value={name}
							onChange={e => setName(e.target.value)}
							placeholder="User Role Name"
						/>
					</label>

					{loading ? (
						<CommonLoadingSpinner loading={true}>{null}</CommonLoadingSpinner>
					) : permsError ? (
						<p className={styles.emptyHint}>Failed to load permissions.</p>
					) : grouped.size === 0 ? (
						<p className={styles.emptyHint}>No permissions available.</p>
					) : (
						Array.from(grouped.entries()).map(([module, subs]) => (
							<section key={module} className={styles.module}>
								<h3 className={styles.moduleTitle}>{module} Permission</h3>
								{Array.from(subs.entries()).map(([subModule, perms]) => (
									<div key={subModule} className={styles.card}>
										<div className={styles.cardTitle}>{subModule}</div>
										<div className={styles.cardActions}>
											{perms.map(p => (
												<CommonCheckBox
													key={p.id}
													title={p.action}
													selected={selected.has(p.id)}
													followState
													onToggle={() => toggle(p.id)}
													className={styles.permissionCheck}
												/>
											))}
										</div>
									</div>
								))}
							</section>
						))
					)}
				</div>

				<div className={styles.dialogFooter}>
					<Button text="Cancel" variant="outline" onClick={onClose} />
					<Button text="Apply" onClick={handleApply} disabled={creating || editing} />
				</div>
			</div>
		</CommonSideDialog>
	);
};

export default RolePermissionsSideDialog;
